// // Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { CircularProgress, Typography } from '@mui/material'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import { SectionHeader } from '@react/components/common/SectionHeader'
import ViewField from '@react/components/elements/ViewField'
import { fetchDefiExchangeRate } from '@react/fetchers/defiFetchers'
import { useOtChainState, useOtSelector } from '@react/redux/store'
import { DefiCollateralSchema } from '@shared/CollateralSetCTO'
import { FeeAccrualCTOSchema } from '@shared/FeeAccrualCTO'
import { PoolDynamicOverviewCTO } from '@shared/PoolDynamicCTO'
import { EthAddr } from '@shared/SchemaTypes'
import { erc20WeiToErc20DecString } from '@shared/conversions'
import { DefiExchangeRateCTO, DefiExchangeRateCTOSchema, ExchangeRateChangeableStatus } from '@shared/defiCTO'
import { logOT } from '@shared/loggingShared'
import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import DefiExchangeRateCard from './DefiExchangeRateCard'

export default function DefiExchangeRatePage(props: { poolAddr?: EthAddr }): React.ReactElement {
  const p = useParams().poolAddr
  const poolAddr = (props.poolAddr ? props.poolAddr : p) as EthAddr
  const {
    stageConfiguration: { serverApiBaseUrl, restAPIKey },
  } = useOtChainState()
  const [defiExchangeRateCTO, setDefiExchangeRateCTO] = useState<DefiExchangeRateCTO>()

  const poolCTO = useOtSelector((state) => state.poolState.poolOverviewCTOs[poolAddr] as PoolDynamicOverviewCTO)
  const refreshCallback = () => {
    if (poolAddr && serverApiBaseUrl && restAPIKey) {
      fetchDefiExchangeRate(serverApiBaseUrl, restAPIKey, poolAddr)
        .then((_defiExchangeRateCTO) => {
          setDefiExchangeRateCTO(_defiExchangeRateCTO)
        })
        .catch((error) => {
          logOT.error('Error fetching defi exchange rate check cto:', error)
        })
    }
  }

  useEffect(() => {
    refreshCallback()
  }, [poolAddr, serverApiBaseUrl, restAPIKey])

  const isReconciled = true

  const liquidityTokenSymbol = poolCTO.liquidityTokenSymbol
  const poolTokenSymbol = poolCTO.symbol
  logOT.info('defiExchangeRateCTO', defiExchangeRateCTO)
  if (!defiExchangeRateCTO) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          marginTop: 10,
        }}
      >
        <Typography variant="h4">Exchange Rate Details</Typography>
        <CircularProgress />
      </Box>
    )
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
      <Typography variant="h4">Exchange Rate Details</Typography>

      <DefiExchangeRateCard
        poolAddr={poolAddr}
        defiExchangeRateCTO={defiExchangeRateCTO}
        inFullPage={true}
        refreshCallback={refreshCallback}
      />
      <Paper
        sx={{
          padding: 2,
          width: '100%',
          backgroundColor: isReconciled ? 'paper.default' : '#FFD580',
        }}
      >
        <SectionHeader>Calibrated Exchange Rate</SectionHeader>
        <Typography variant="caption">
          The calibrated exchange rate shows what the exchange rate is set to by default. It’s based on the original
          $1,000 deposited into the vault, which started at an exchange rate of 1.0. The current value reflects how that
          initial $1,000 has changed over time
        </Typography>

        <Box sx={{ display: 'flex', marginTop: 12, flexDirection: 'row', gap: 2, alignItems: 'center' }}>
          <Box sx={{ display: 'flex', flex: 3 }}>
            <ViewField
              fieldSchema={DefiExchangeRateCTOSchema.describe().fields.calibratedExchangeRate}
              liquidityTokenSymbol={liquidityTokenSymbol}
              poolTokenSymbol={poolTokenSymbol}
              dataObject={defiExchangeRateCTO}
            />
          </Box>
          <Box sx={{ display: 'flex', flex: 0.2 }}>
            <Typography variant="h2">=</Typography>
          </Box>
          <Box sx={{ display: 'flex', flex: 5, flexDirection: 'column', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Calibrated Balance /1000 * OtShares - Lifetime Fees
            </Typography>
            <hr style={{ width: '80%', margin: '4px 0' }} />
            <Typography variant="h6">OtShares</Typography>
          </Box>
          <Box sx={{ display: 'flex', flex: 0.2 }}>
            <Typography variant="h2">=</Typography>
          </Box>
          <Box sx={{ display: 'flex', flex: 5, flexDirection: 'column', gap: 2, alignItems: 'center' }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              {erc20WeiToErc20DecString(defiExchangeRateCTO.calibratedBalance)} /1000 *{' '}
              {erc20WeiToErc20DecString(defiExchangeRateCTO.otShares)}-{' '}
              {erc20WeiToErc20DecString(defiExchangeRateCTO.feeAccrualCTO.lifetimeAccruedFees)}
            </Typography>

            <hr style={{ width: '100%', margin: '4px 0' }} />

            <Typography variant="h6"> {erc20WeiToErc20DecString(defiExchangeRateCTO.otShares)}</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', marginTop: 12, flexDirection: 'row', gap: 2, alignItems: 'center' }}>
          <Box sx={{ display: 'flex', flex: 2, flexDirection: 'column', gap: 2 }}>
            <ViewField
              fieldSchema={DefiExchangeRateCTOSchema.describe().fields.calibratedBalance}
              liquidityTokenSymbol={liquidityTokenSymbol}
              poolTokenSymbol={poolTokenSymbol}
              dataObject={defiExchangeRateCTO}
            />
          </Box>
          <Box sx={{ display: 'flex', flex: 2, flexDirection: 'column', gap: 2 }}>
            <ViewField
              fieldSchema={DefiExchangeRateCTOSchema.describe().fields.otShares}
              liquidityTokenSymbol={liquidityTokenSymbol}
              poolTokenSymbol={poolTokenSymbol}
              dataObject={defiExchangeRateCTO}
            />
          </Box>
          <Box sx={{ display: 'flex', flex: 2, flexDirection: 'column', gap: 2 }}>
            <ViewField
              fieldSchema={FeeAccrualCTOSchema.describe().fields.lifetimeAccruedFees}
              liquidityTokenSymbol={liquidityTokenSymbol}
              poolTokenSymbol={poolTokenSymbol}
              dataObject={defiExchangeRateCTO.feeAccrualCTO}
            />
          </Box>
        </Box>
      </Paper>

      {defiExchangeRateCTO.nativeDefiExchangeRate === '0' && (
        <Paper
          sx={{
            padding: 2,
            backgroundColor: isReconciled ? 'paper.default' : '#FFD580',
          }}
        >
          <SectionHeader>Native Defi Exchange Rate</SectionHeader>
          <Typography variant="caption">
            This Defi vault does not have a native defi exchange rate so it is not available.
          </Typography>
        </Paper>
      )}
      {defiExchangeRateCTO.nativeDefiExchangeRate !== '0' && (
        <Paper
          sx={{
            padding: 2,
            width: '100%',
            backgroundColor: isReconciled ? 'paper.default' : '#FFD580',
          }}
        >
          <SectionHeader>Native Defi Exchange Rate</SectionHeader>
          <Typography variant="caption">
            The native DeFi exchange rate is calculated from the underlying DeFi share or token rate by converting those
            shares into asset value. This rate is then normalized so that it matches the calibrated exchange rate at the
            starting point.
          </Typography>
          <Box sx={{ display: 'flex', marginTop: 12, flexDirection: 'row', gap: 2, alignItems: 'center' }}>
            <Box sx={{ display: 'flex', flex: 1 }}>
              <ViewField
                fieldSchema={DefiExchangeRateCTOSchema.describe().fields.nativeDefiExchangeRate}
                liquidityTokenSymbol={liquidityTokenSymbol}
                poolTokenSymbol={poolTokenSymbol}
                dataObject={defiExchangeRateCTO}
              />
            </Box>
            <Box sx={{ display: 'flex', flex: 3 }}>
              <Box sx={{ display: 'flex', flex: 1, gap: 0 }}>
                {/* Left column: Vertically centered */}
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    height: 80,
                    minHeight: 80,
                    py: 1,
                  }}
                >
                  <Typography variant="h6" sx={{}}>
                    (Scale*DefiTotalAssets / DefiTotalShares ) * OtShares - Lifetime Fees
                  </Typography>
                  <hr style={{ width: '95%', margin: '4px 0' }} />
                  <Typography variant="h6" sx={{}}>
                    OtShares
                  </Typography>
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                    minHeight: 80, // ensure some min height so centering occurs
                  }}
                >
                  <Typography variant="h6" sx={{ whiteSpace: 'nowrap' }}>
                    {(Number(defiExchangeRateCTO.collateralCTO.nativeDefiExchangeRateScale) / 1e18).toFixed(12)} *{' '}
                    {erc20WeiToErc20DecString(defiExchangeRateCTO.defiTotalAssets)} /
                    {erc20WeiToErc20DecString(defiExchangeRateCTO.defiTotalShares)} -{' '}
                    {erc20WeiToErc20DecString(defiExchangeRateCTO.feeAccrualCTO.lifetimeAccruedFees)}
                  </Typography>
                  <hr style={{ width: '100%', margin: '4px 0' }} />
                  <Typography variant="h6"> {erc20WeiToErc20DecString(defiExchangeRateCTO.otShares)}</Typography>
                </Box>
                {/* Right column: "Assets" on top, horizontal line, "shares" on bottom */}
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', marginTop: 12, flexDirection: 'row', gap: 2, alignItems: 'center' }}>
            <Box sx={{ display: 'flex', flex: 2, flexDirection: 'column', gap: 2 }}>
              <ViewField
                fieldSchema={DefiCollateralSchema.describe().fields.nativeDefiExchangeRateScale}
                liquidityTokenSymbol={liquidityTokenSymbol}
                poolTokenSymbol={poolTokenSymbol}
                dataObject={defiExchangeRateCTO.collateralCTO}
              />
            </Box>
            <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column', gap: 2 }}>
              <ViewField
                fieldSchema={DefiExchangeRateCTOSchema.describe().fields.defiTotalAssets}
                liquidityTokenSymbol={liquidityTokenSymbol}
                poolTokenSymbol={poolTokenSymbol}
                dataObject={defiExchangeRateCTO}
              />
            </Box>
            <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column', gap: 2 }}>
              <ViewField
                fieldSchema={DefiExchangeRateCTOSchema.describe().fields.defiTotalShares}
                liquidityTokenSymbol={liquidityTokenSymbol}
                poolTokenSymbol={poolTokenSymbol}
                dataObject={defiExchangeRateCTO}
              />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', marginTop: 12, flexDirection: 'row', gap: 2, alignItems: 'center' }}>
            <Box sx={{ display: 'flex', flex: 1 }}>
              <ViewField
                fieldSchema={DefiExchangeRateCTOSchema.describe().fields.isNativeDefiReconciled}
                liquidityTokenSymbol={liquidityTokenSymbol}
                poolTokenSymbol={poolTokenSymbol}
                dataObject={defiExchangeRateCTO}
              />
            </Box>
            <Box sx={{ display: 'flex', flex: 6 }}>
              <Typography variant="caption">
                true indicates that the native DeFi exchange rate is reconciled is within the variance of the calibrated
                exchange rate
              </Typography>
            </Box>
            <Box
              sx={{
                border:
                  !defiExchangeRateCTO.isNativeDefiReconciled &&
                  defiExchangeRateCTO.exchangeRateChangeableStatus ===
                    ExchangeRateChangeableStatus.DiscrepancyDetectedOnHold
                    ? ' 3px dashed red'
                    : '',
              }}
            >
              <ViewField
                fieldSchema={DefiCollateralSchema.describe().fields.resolvedVariance}
                liquidityTokenSymbol={liquidityTokenSymbol}
                poolTokenSymbol={poolTokenSymbol}
                dataObject={defiExchangeRateCTO.collateralCTO}
              />
            </Box>
          </Box>
        </Paper>
      )}
      <Paper
        sx={{
          padding: 2,
          width: '100%',
          backgroundColor: BigInt(defiExchangeRateCTO.totalPoolAssets) > 0n ? 'paper.default' : '#FFD580',
        }}
      >
        <SectionHeader>Asset Exchange Rate</SectionHeader>
        {BigInt(defiExchangeRateCTO.totalPoolAssets) == 0n && (
          <Typography variant="h2">No assets in the pool so this is not available</Typography>
        )}
        <Typography variant="caption">
          The asset-based exchange rate reflects all assets attributed to this OT vault — including those invested in
          the DeFi vault, held in transit within the pool or transition wallet, and those attributed in the Circle
          wallet. For now, the Circle wallet balance is assumed to be zero. If assets are later attributed there, they
          may cause a small variance but won’t prevent the exchange rate from updating.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 20, alignItems: 'center', marginTop: 12 }}>
          <Box sx={{ display: 'flex', flex: 1, alignItems: 'center' }}>
            <ViewField
              fieldSchema={DefiExchangeRateCTOSchema.describe().fields.assetBasedExchangeRate}
              liquidityTokenSymbol={liquidityTokenSymbol}
              poolTokenSymbol={poolTokenSymbol}
              dataObject={defiExchangeRateCTO}
            />
          </Box>
          <Box sx={{ display: 'flex', flex: 3, flexDirection: 'column', gap: 2, alignItems: 'center' }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              {erc20WeiToErc20DecString(defiExchangeRateCTO.defiOTAssets)} -
              {erc20WeiToErc20DecString(defiExchangeRateCTO.feeAccrualCTO.feeAccrual)}
            </Typography>

            <hr style={{ width: '100%', margin: '4px 0' }} />

            <Typography variant="h6"> {erc20WeiToErc20DecString(defiExchangeRateCTO.otShares)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', flex: 3, flexDirection: 'column', gap: 2, alignItems: 'center' }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              OtDefiAssets - Fees Accrued
            </Typography>

            <hr style={{ width: '100%', margin: '4px 0' }} />

            <Typography variant="h6"> OtShares</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'center', marginTop: 12 }}>
          <Box sx={{ display: 'flex', flex: 1, alignItems: 'center' }}>
            <ViewField
              fieldSchema={DefiExchangeRateCTOSchema.describe().fields.defiOTAssets}
              liquidityTokenSymbol={liquidityTokenSymbol}
              poolTokenSymbol={poolTokenSymbol}
              dataObject={defiExchangeRateCTO}
            />
          </Box>
          <Box sx={{ display: 'flex', flex: 2, flexDirection: 'column', gap: 2 }}>
            <ViewField
              fieldSchema={FeeAccrualCTOSchema.describe().fields.feeAccrual}
              liquidityTokenSymbol={liquidityTokenSymbol}
              poolTokenSymbol={poolTokenSymbol}
              dataObject={defiExchangeRateCTO.feeAccrualCTO}
            />
          </Box>

          <Box sx={{ display: 'flex', flex: 1, alignItems: 'center', flexDirection: 'column' }}>
            <ViewField
              fieldSchema={DefiExchangeRateCTOSchema.describe().fields.otShares}
              liquidityTokenSymbol={liquidityTokenSymbol}
              poolTokenSymbol={poolTokenSymbol}
              dataObject={defiExchangeRateCTO}
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', marginTop: 12, flexDirection: 'row', gap: 2, alignItems: 'center' }}>
          <Box sx={{ display: 'flex', flex: 1 }}>
            <ViewField
              fieldSchema={DefiExchangeRateCTOSchema.describe().fields.isAssetsReconciled}
              liquidityTokenSymbol={liquidityTokenSymbol}
              poolTokenSymbol={poolTokenSymbol}
              dataObject={defiExchangeRateCTO}
            />
          </Box>
          <Box sx={{ display: 'flex', flex: 6 }}>
            <Typography variant="caption">
              true indicates that the asset-based exchange rate is reconciled is within the variance of the calibrated
              exchange rate
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'center', marginTop: 12 }}>
          <Box sx={{ display: 'flex', flex: 1, alignItems: 'center' }}>
            <ViewField
              fieldSchema={DefiExchangeRateCTOSchema.describe().fields.assetDelta}
              liquidityTokenSymbol={liquidityTokenSymbol}
              poolTokenSymbol={poolTokenSymbol}
              dataObject={defiExchangeRateCTO}
            />
          </Box>
          <Box sx={{ display: 'flex', flex: 1, alignItems: 'center' }}>
            <Typography variant="h2">=</Typography>
          </Box>
          <Box sx={{ display: 'flex', flex: 1 }}>
            <ViewField
              fieldSchema={DefiExchangeRateCTOSchema.describe().fields.defiOTAssets}
              liquidityTokenSymbol={liquidityTokenSymbol}
              poolTokenSymbol={poolTokenSymbol}
              dataObject={defiExchangeRateCTO}
            />
          </Box>
          <Box sx={{ display: 'flex', flex: 1, alignItems: 'center' }}>
            <Typography variant="h2">-</Typography>
          </Box>

          <Box sx={{ display: 'flex', flex: 1 }}>
            <ViewField
              fieldSchema={DefiExchangeRateCTOSchema.describe().fields.totalPoolAssets}
              liquidityTokenSymbol={liquidityTokenSymbol}
              poolTokenSymbol={poolTokenSymbol}
              dataObject={defiExchangeRateCTO}
            />
          </Box>
          <Box sx={{ display: 'flex', flex: 1, alignItems: 'center' }}>
            <Typography variant="h2">-</Typography>
          </Box>
          <Box sx={{ display: 'flex', flex: 2, flexDirection: 'column', gap: 2 }}>
            <ViewField
              fieldSchema={FeeAccrualCTOSchema.describe().fields.feeAccrual}
              liquidityTokenSymbol={liquidityTokenSymbol}
              poolTokenSymbol={poolTokenSymbol}
              dataObject={defiExchangeRateCTO.feeAccrualCTO}
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'center', marginTop: 12 }}>
          <Box
            sx={{
              border: defiExchangeRateCTO.isAssetsReconciled ? '' : ' 3px dashed red',
            }}
          >
            <ViewField
              fieldSchema={DefiExchangeRateCTOSchema.describe().fields.isAssetsReconciled}
              liquidityTokenSymbol={liquidityTokenSymbol}
              poolTokenSymbol={poolTokenSymbol}
              dataObject={defiExchangeRateCTO}
            />
          </Box>
          <Box
            sx={{
              border:
                !defiExchangeRateCTO.isAssetsReconciled &&
                defiExchangeRateCTO.exchangeRateChangeableStatus ===
                  ExchangeRateChangeableStatus.DiscrepancyDetectedOnHold
                  ? ' 3px dashed red'
                  : '',
            }}
          >
            <ViewField
              fieldSchema={DefiCollateralSchema.describe().fields.assetTolerance}
              liquidityTokenSymbol={liquidityTokenSymbol}
              poolTokenSymbol={poolTokenSymbol}
              dataObject={defiExchangeRateCTO.collateralCTO}
            />
          </Box>
          {!defiExchangeRateCTO.isAssetsReconciled && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 5,
                paddingRight: 10,
                border:
                  !defiExchangeRateCTO.isAssetsReconciled &&
                  defiExchangeRateCTO.exchangeRateChangeableStatus ===
                    ExchangeRateChangeableStatus.DiscrepancyDetectedOnHold
                    ? ' 3px dashed red'
                    : ' ',
              }}
            >
              <Typography variant="h2"> {'<'} </Typography>
              <ViewField
                fieldSchema={DefiExchangeRateCTOSchema.describe().fields.assetDelta}
                liquidityTokenSymbol={liquidityTokenSymbol}
                poolTokenSymbol={poolTokenSymbol}
                dataObject={defiExchangeRateCTO}
              />
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  )
}
